import { JSONContent } from '@tiptap/core';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Content,
  Header,
  Heading,
  Icon,
  SidebarTrigger,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { dynamicArtifactTemplateSchema } from '@eluve/llm-outputs';
import { useCaptureEventOnMount } from '@eluve/posthog-react';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { ModelPreset } from './ModelPresetSlider';
import { TemplateBundleWorkspace } from './TemplateBundleWorkspace';
import {
  docToDynamicArtifact,
  dynamicArtifactToDoc,
  makeDefaultSectionsDoc,
} from './prompt-editor/templateSectionsDoc';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

/**
 * "Start from scratch" template creation experience (ENG-4547). The
 * authoring workspace is shared with the edit page; saving creates the
 * template (a template bundle under the hood) and lands on its edit
 * page where versions are managed.
 */
export const NewTemplateBundlePage: React.FC = () => {
  useCaptureEventOnMount('page_view_new_template_bundle');

  const { t } = useTranslation();
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Template generated from an uploaded note (set by the create wizard's
  // upload step via navigation state) seeds the editor instead of the
  // default scaffold
  const generatedTemplate = useMemo(() => {
    const state = location.state as { generatedTemplate?: unknown } | null;
    if (!state?.generatedTemplate) {
      return null;
    }
    const parsed = dynamicArtifactTemplateSchema.safeParse(
      state.generatedTemplate,
    );
    return parsed.success ? parsed.data : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [templateName, setTemplateName] = useState(
    generatedTemplate?.name ?? '',
  );
  const [description, setDescription] = useState(
    generatedTemplate?.description ?? '',
  );
  const [selectedPreset, setSelectedPreset] = useState<ModelPreset | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const {
    markDirty,
    markClean,
    dialog: unsavedChangesDialog,
  } = useUnsavedChangesGuard();

  // The slider reports its initial selection once on mount; only a later
  // (user-driven) preset change should dirty the page.
  const presetNotified = useRef(false);
  const handlePresetSelected = useCallback(
    (preset: ModelPreset) => {
      setSelectedPreset(preset);
      if (presetNotified.current) {
        markDirty();
      } else {
        presetNotified.current = true;
      }
    },
    [markDirty],
  );

  const initialDoc = useMemo(
    () =>
      generatedTemplate
        ? dynamicArtifactToDoc(generatedTemplate)
        : makeDefaultSectionsDoc(),
    [generatedTemplate],
  );
  const docRef = useRef<JSONContent>(initialDoc);

  const handleSave = async (isDraft: boolean) => {
    if (!tenantId) {
      return;
    }
    const name = templateName.trim();
    if (!name) {
      toast.error(t('outputTemplates.common.nameRequired'));
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiClient.llm.createTemplateBundle({
        params: { tenantId },
        body: {
          name,
          description: description.trim() || undefined,
          outputTemplate: docToDynamicArtifact({ doc: docRef.current, name }),
          modelPresetId: selectedPreset?.id,
          isDraft,
        },
      });

      if (response.status === 201) {
        toast.success(
          isDraft
            ? t('outputTemplates.common.draftSaved')
            : t('outputTemplates.new.templateSaved'),
        );
        // Clear before navigating so the guard doesn't catch its own
        // post-save navigation
        markClean();
        navigate(`../bundles/${response.body.outputTemplateId}`);
      } else if (response.status === 422) {
        toast.error(
          response.body.message ?? t('outputTemplates.new.saveFailed'),
        );
      } else {
        toast.error(t('outputTemplates.new.saveFailed'));
      }
    } catch (error) {
      toast.error(t('outputTemplates.new.saveFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Header>
        <SidebarTrigger />
        <Link to=".." aria-label={t('outputTemplates.common.backToTemplates')}>
          <Button variant="ghost" size="sm">
            <Icon name="ArrowLeft" size="md" />
          </Button>
        </Link>
        <div className="my-2 py-2">
          <Heading>{t('outputTemplates.new.title')}</Heading>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            disabled={isSaving}
            onClick={() => handleSave(true)}
          >
            {t('outputTemplates.new.saveDraft')}
          </Button>
          <Button disabled={isSaving} onClick={() => handleSave(false)}>
            {isSaving
              ? t('outputTemplates.common.saving')
              : t('outputTemplates.new.saveTemplate')}
          </Button>
        </div>
      </Header>
      <Content>
        {/* Match the patient pages' content inset */}
        <div className="py-3 md:p-8">
          <TemplateBundleWorkspace
            templateName={templateName}
            onTemplateNameChange={(name) => {
              setTemplateName(name);
              markDirty();
            }}
            description={description}
            onDescriptionChange={(value) => {
              setDescription(value);
              markDirty();
            }}
            initialDoc={initialDoc}
            onDocChange={(doc) => {
              docRef.current = doc;
              markDirty();
            }}
            onPresetSelected={handlePresetSelected}
          />
        </div>
      </Content>
      {unsavedChangesDialog}
    </>
  );
};
